import React, { useEffect, useRef, useState } from 'react';
import { Grid, Snackbar } from '@mui/material';
import { useParams, useHistory } from 'react-router-dom';
import _ from 'lodash';
import LinearProgress from '@mui/material/LinearProgress';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Alert } from '@mui/material';
import { connect, useSelector } from 'react-redux';
import { CreditLineStatusEnum } from '@trnsact/trnsact-shared-types';
import {
  WorkflowHeader,
  WorkflowDiagram,
  WorkflowDrawer,
  VOStatus,
  WorkflowEventType,
  WorkflowActionType,
  CREATE_WORKFLOW_MUTATION,
  UPDATE_WORKFLOW_MUTATION,
  GET_WORKFLOW_BY_ID,
} from 'components/Workflows';

const WorkflowUpsertPage: React.FC = () => {
  const account = useSelector((state: any) => state.account);
  const { id } = useParams<{ id: string | undefined }>();
  const history = useHistory();
  const drawerContainerRef = useRef<HTMLDivElement | null>(null);
  const isInitialLoad = useRef(true);
  const [workflowName, setWorkflowName] = useState('');
  const [workflowDescription, setWorkflowDescription] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [submitDisabled, setSubmitDisabled] = useState(true);

  const [selectedEvent, setSelectedEvent] = useState<WorkflowEventType>(WorkflowEventType.VENDOR_OPPORTUNITY_UPDATED);
  const [selectedConditions, setSelectedConditions] = useState<(VOStatus | CreditLineStatusEnum)[]>([]);
  const [selectedAction, setSelectedAction] = useState<WorkflowActionType>(WorkflowActionType.CREATE_TASK);
  const [selectedActionConfiguration, setSelectedActionConfiguration] = useState<any>();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [workflowId, setWorkflowId] = useState(id);
  const [editingBox, setEditingBox] = useState<'Event' | 'Condition' | 'Action' | null>(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [getWorkflowByID, { data: workflowData, loading: loadingWorkflow, error: workflowError }] = useLazyQuery(
    GET_WORKFLOW_BY_ID,
    {
      context: { authRequired: true },
      fetchPolicy: 'no-cache',
      onCompleted(data) {
        if (data) {
          setWorkflowName(data.workflow.name);
          setWorkflowDescription(data.workflow.description);
          setEnabled(data.workflow.active);
          const actionConfig = _.get(data.workflow.json.actions, '[0]');

          // Determine action type from configuration
          if (actionConfig?.CREATE_TASK) {
            setSelectedAction(WorkflowActionType.CREATE_TASK);
          } else if (actionConfig?.SEND_EMAIL) {
            setSelectedAction(WorkflowActionType.SEND_EMAIL);
          }

          setSelectedActionConfiguration(actionConfig);
          setSelectedEvent(data.workflow.json.event || WorkflowEventType.VENDOR_OPPORTUNITY_UPDATED);
          setSelectedConditions(data.workflow.json.conditions || []);
          isInitialLoad.current = false;
        }
      },
    }
  );

  useEffect(() => {
    if (id) {
      setWorkflowId(id);
    }
  }, [id]);
  const [createWorkflow, { loading: loadingCreate }] = useMutation(CREATE_WORKFLOW_MUTATION);
  const [updateWorkflow, { loading: loadingUpdate }] = useMutation(UPDATE_WORKFLOW_MUTATION);

  useEffect(() => {
    if (id) {
      getWorkflowByID({ variables: { id } });
    }
  }, [id]);

  useEffect(() => {
    // Clear conditions when event changes, but not during initial load
    if (!isInitialLoad.current) {
      setSelectedConditions([]);
    }
  }, [selectedEvent]);

  useEffect(() => {
    let isActionValid = false;

    if (selectedAction === WorkflowActionType.CREATE_TASK) {
      isActionValid = !_.isEmpty(_.get(selectedActionConfiguration, 'CREATE_TASK.taskItems', []));
    } else if (selectedAction === WorkflowActionType.SEND_EMAIL) {
      isActionValid = !_.isEmpty(_.get(selectedActionConfiguration, 'SEND_EMAIL.templateId', ''));
    }

    const isFormValid =
      workflowName.trim() !== '' &&
      workflowDescription.trim() !== '' &&
      isActionValid &&
      selectedConditions.length > 0 &&
      selectedEvent !== null;
    const loadingState = loadingCreate || loadingUpdate || loadingWorkflow;
    setSubmitDisabled(!isFormValid || loadingState);
  }, [
    workflowName,
    workflowDescription,
    selectedAction,
    selectedConditions,
    selectedActionConfiguration,
    selectedEvent,
    loadingCreate,
    loadingUpdate,
  ]);

  const handleBoxClick = (box: 'Event' | 'Condition' | 'Action') => {
    setEditingBox(box);
    setDrawerOpen(true);
  };

  const handleSave = () => {
    setDrawerOpen(false);
  };

  const handleDeleteCondition = (index: number) => {
    const newConditions = selectedConditions.filter((_, i) => i !== index);
    setSelectedConditions(newConditions);
  };

  const handleSaveTask = (payload: any) => {
    try {
      //parse and stringify to remove undefined references
      setSelectedActionConfiguration({
        [WorkflowActionType.CREATE_TASK]: { ...JSON.parse(JSON.stringify(payload)), taskId: '-1' },
      });
    } catch (err) {
      console.log(err);
    }
  };

  const handleSaveEmail = (payload: any) => {
    try {
      setSelectedActionConfiguration({
        [WorkflowActionType.SEND_EMAIL]: { ...payload },
      });
    } catch (err) {
      console.log(err);
    }
  };

  const handleSaveWorkflow = async () => {
    try {
      const input = {
        json: {
          event: selectedEvent,
          conditions: selectedConditions,
          actions: [selectedActionConfiguration],
        },
        description: workflowDescription,
        active: enabled,
        name: workflowName,
      };
      let success;
      let response;
      if (id) {
        response = await updateWorkflow({ variables: { input: { ...input, id } } });
        success = _.get(response, 'data.updateWorkflow.success', false);
      } else {
        response = await await createWorkflow({ variables: { input } });
        success = _.get(response, 'data.createWorkflow.success', false);
      }

      if (success) {
        setSnackbarSeverity('success');
        setSnackbarMessage(`Workflow saved successfully! `);
        if (_.get(response, 'data.createWorkflow.id', '')) {
          setTimeout(() => {
            history.push('/workflows/edit/' + _.get(response, 'data.createWorkflow.id', ''));
          }, 1000);
        }
      } else {
        setSnackbarSeverity('error');
        setSnackbarMessage(`Failed to save workflow`);
      }
    } catch (err) {
      console.error('Error saving workflow:', err);
      setSnackbarSeverity('error');
      setSnackbarMessage('An error occurred while saving the workflow. Please try again.');
    } finally {
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  if (loadingWorkflow || loadingUpdate) {
    return <LinearProgress variant="indeterminate" color="primary" />;
  }

  return (
    <div>
      <Grid container spacing={2}>
        <WorkflowHeader
          workflowId={workflowId}
          workflowName={workflowName}
          workflowDescription={workflowDescription}
          enabled={enabled}
          submitDisabled={submitDisabled}
          onBackClick={() => (window.location.href = '/workflows')}
          onNameChange={setWorkflowName}
          onDescriptionChange={setWorkflowDescription}
          onEnabledChange={setEnabled}
          onSave={handleSaveWorkflow}
        />

        <WorkflowDiagram
          selectedEvent={selectedEvent}
          selectedConditions={selectedConditions}
          selectedActionConfiguration={selectedActionConfiguration}
          onEventClick={() => handleBoxClick('Event')}
          onConditionClick={() => handleBoxClick('Condition')}
          onActionClick={() => handleBoxClick('Action')}
          onDeleteCondition={handleDeleteCondition}
          drawerContainerRef={drawerContainerRef}
        />

        <WorkflowDrawer
          open={drawerOpen}
          editingBox={editingBox}
          selectedEvent={selectedEvent}
          selectedConditions={selectedConditions}
          selectedAction={selectedAction}
          selectedActionConfiguration={selectedActionConfiguration}
          accountId={account.id}
          accountName={account.name}
          onClose={() => setDrawerOpen(false)}
          onSave={handleSave}
          onEventChange={setSelectedEvent}
          onConditionsChange={setSelectedConditions}
          onActionChange={setSelectedAction}
          onSaveTask={handleSaveTask}
          onSaveEmail={handleSaveEmail}
        />
      </Grid>
      {/* Snackbar Component */}
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

const mapStateToProps = (state: any) => {
  return {
    account: state.account,
  };
};

export default connect(mapStateToProps, null)(WorkflowUpsertPage);
